<template>
  <div class="snowfall-chart">
    <div class="snowfall-chart-header">
      <h1 class="title">{{ $t('Snowfall Chart') }}</h1>
      <!-- Type selector here -->
      <chartToggles />
      <chartUnitToggles />
    </div>

    <span class="arrow left-arrow" @click.prevent="backOnePeriod" :class="{'active': enableBackArrow}"></span>

    <apexChart ref="snowfallChart" :options="options" height="250" :series="series" v-if="allSnowfallData"></apexChart>

    <div class="current-period">
      {{ currentPeriod }}
    </div>

    <span class="arrow right-arrow" @click.prevent="forwardOnePeriod" :class="{'active': enableForwardArrow}"></span>
  </div>
</template>

<script>
import apexChart from 'vue-apexcharts';
import { mapState } from 'vuex';
import chartToggles from './Snowfall/ChartToggles.vue';
import chartUnitToggles from './Snowfall/ChartUnitToggles.vue';

export default {
  name: 'SnowfallChart',
  components: { apexChart, chartToggles, chartUnitToggles },
  created() {
    this.$store.dispatch('getSnowfallChartData');
  },
  watch: {
    allSnowfallData() {
      this.setChartData();
    },
    'snowfallChartSettings.showTotalSnowfall': function () {
      this.setChartData();
    },
    'snowfallChartSettings.snowfallInMetric': function () {
      this.setChartData();
    },
    'snowfallChartSettings.snowfallTimeRange': function () {
      this.currentIndex = 0;
      this.setChartData();
    },
  },
  data() {
    return {
      currentAnnualRange: [`${(new Date()).getFullYear() - 1}-${(new Date()).getFullYear()}`],
      currentIndex: 0,
      options: {
        chart: {
          id: 'mtnfeed-snowfall-chart',
          animations: { enabled: false },
          fontFamily: 'proxima-regular, Arial, sans-serif',
          selection: { enabled: false },
          toolbar: {
            tools: {
              download: false,
              pan: false,
              zoomin: false,
              zoomout: false,
              zoom: false,
            },
          },
          zoom: {
            zoomedArea: {
              fill: {
                color: '#ccc',
                opacity: 0.1,
              },
              stroke: {
                dashArray: 10,
                color: '#000',
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [1],
          formatter: (val) => {
            if (typeof (val) === 'undefined') { return ''; }
            if (val === 0) { return ''; }

            return `${Math.ceil(val)}${this.currentUnit}`;
          },
          offsetX: 1,
          offsetY: -10,
          background: {
            enabled: false,
          },
        },
        grid: {
          show: true,
        },
        legend: {
          markers: {
            height: 5,
            width: 25,
          },
        },
        markers: {
          size: 5,
          strokeColors: '#fff',
          strokeWidth: 2,
          offsetX: -1,
        },
        plotOptions: {
          bar: {
            columnWidth: 5,
          },
        },
        fill: {
          opacity: [0.5, 1.0],
        },
        stroke: {
          width: [2, 2],
          curve: 'smooth',
        },
        xaxis: {
          type: 'datetime',
          labels: {
            format: 'd MMM.',
          },
        },
        yaxis: [
          {
            labels: {
              show: true,
              formatter: val => `${Math.round(val * 100) / 100}${this.currentUnit}`,
              minWidth: 10,
            },
          },
          {
            labels: {
              show: false,
              minWidth: 10,
            },
          },
        ],
        tooltip: {
          enabled: true,
          theme: false,
          y: {
            formatter: (val) => {
              if (typeof (val) === 'undefined') { return ''; }

              return `${Math.ceil(val)}${this.currentUnit}`;
            },
          },
        },
      },
      series: [],
    };
  },
  computed: {
    ...mapState(['allSnowfallData', 'snowfallChartSettings']),
    currentUnit() {
      return this.snowfallChartSettings.snowfallInMetric ? 'cm' : '"';
    },
    currentMeasure() {
      return this.snowfallChartSettings.snowfallInMetric ? 'metric' : 'imperial';
    },
    currentPeriod() {
      if (this.allSnowfallData.length === 0) { return; }

      return this.currentSnowfallData().key;
    },
    enableBackArrow() {
      if (this.allSnowfallData.length === 0) { return; }

      return this.currentIndex + 1 < this.currentSnowfallDataSize;
    },
    enableForwardArrow() {
      if (this.allSnowfallData.length === 0) { return; }

      return this.currentIndex > 0;
    },
    currentSnowfallDataSize() {
      if (this.allSnowfallData.length === 0) { return; }

      const timeRange = this.snowfallChartSettings.snowfallTimeRange;
      return this.allSnowfallData[timeRange].length;
    },
  },
  methods: {
    backOnePeriod() {
      if (this.currentIndex + 1 >= this.currentSnowfallDataSize) { return; }

      this.currentIndex = this.currentIndex + 1;
      this.setChartData();
    },
    forwardOnePeriod() {
      if (this.currentIndex <= 0) { return; }

      this.currentIndex = this.currentIndex - 1;
      this.setChartData();
    },
    setChartData() {
      this.series = [
        {
          name: this.$t('Total Snowfall'),
          data: this.culmSnowfallForCurrent(),
          type: 'area',
          color: this.snowfallChartSettings.colors[1],
        },
        {
          name: this.snowfallChartSettings.stormSnowfallText || this.$t('Storm Snowfall'),
          data: this.currentSnowfallDataByMeasure(),
          type: 'column',
          color: this.snowfallChartSettings.colors[0],
        },
      ];
    },
    currentSnowfallData() {
      const timeRange = this.snowfallChartSettings.snowfallTimeRange;
      return this.allSnowfallData[timeRange][this.currentIndex];
    },
    currentSnowfallDataByMeasure() {
      return this.currentSnowfallData()[this.currentMeasure];
    },
    culmSnowfallForCurrent() {
      if (this.snowfallChartSettings.showTotalSnowfall == false) {
        return [];
      }

      const snowfallData = [];
      let total = 0;
      this.currentSnowfallDataByMeasure().forEach((part, index) => {
        total += part.y * 1.0;
        snowfallData[index] = { x: part.x, y: Math.ceil(total) };
      }, snowfallData);

      return snowfallData;
    },
  },
};
</script>

<style lang="scss">
  .snowfall-chart {
    position: relative;
    @media (max-width: $tablet) {
      display: none;
    }
    .snowfall-chart-header {
      display: flex;
      justify-content: space-between;
    }

    .current-period {
      color: $dark-gray;
      font-family: $base-font-regular;
      font-size: 12px;
      line-height: 1;
      margin: -20px 0 0 5%;
    }

    .apexcharts-tooltip {
      background: #ffffff !important;
      border: 1px solid #000;
      box-shadow: 0 0 black;
      color: black;
      .apexcharts-tooltip-title {
        border-bottom: 1px solid black;
      }
    }
    .apexcharts-reset-icon {
      background: url('~@/assets/icons/undo.svg') no-repeat center center;
      svg {
        display: none;
      }
    }
    span.arrow {
      background: $white url('~@/assets/icons/right-arrow.svg') no-repeat center center;
      background-size: contain;
      cursor: none;
      display: inline-block;
      height: 50px;
      margin: 0;
      opacity: 0.2;
      padding: 5px 10px;
      pointer-events: none;
      position: absolute;
      top: 50%;
      vertical-align: middle;
      transition: opacity 0.5 ease-in-out;
      &.active {
        cursor: pointer;
        opacity: 1.0;
        pointer-events: all;
      }
      &:hover {
        opacity: 0.5;
      }
      &.right-arrow {
        right: 0.5%;
      }
      &.left-arrow {
        left: 1.5%;
        transform: rotate(180deg);
      }
    }
  }
</style>

<i18n locale="fr-ca">
{
  "Total Snowfall": "Précipitations de neige totales",
  "Storm Snowfall": "Précipitations de neige par tempête",
  "Snowfall Chart": "Graphique Des Précipitations de Neige"
}
</i18n>
